import { useEffect } from "react";

/**
 * Custom hook to set page title and meta description dynamically
 * @param {Object} options - Meta options
 * @param {string} options.title - Page title
 * @param {string} options.description - Meta description
 * @param {string} options.ogTitle - Open Graph title (optional, defaults to title)
 * @param {string} options.ogDescription - Open Graph description (optional, defaults to description)
 * @param {string} options.canonicalUrl - Canonical URL (optional)
 */
export function usePageMeta({
  title,
  description,
  ogTitle,
  ogDescription,
  canonicalUrl,
}) {
  useEffect(() => {
    // Set document title
    if (title) {
      const setTitle = () => {
        document.title = title;
        const titleElement = document.querySelector("title");
        if (titleElement) {
          titleElement.textContent = title;
        }
      };
      
      // Set title immediately
      setTitle();
      
      // Also set it after delays to catch any scripts that might override it
      setTimeout(setTitle, 0);
      setTimeout(setTitle, 50);
      setTimeout(setTitle, 100);
      setTimeout(setTitle, 200);
      setTimeout(setTitle, 500);
      setTimeout(setTitle, 1000);
      
      // Set up an interval to continuously monitor and restore the title
      // This ensures the title persists even if external scripts try to change it
      const intervalId = setInterval(() => {
        if (document.title !== title) {
          setTitle();
        }
      }, 500);
      
      // Cleanup
      return () => {
        clearInterval(intervalId);
      };
    }
  }, [title]);

  useEffect(() => {
    // Set or update meta description
    if (description) {
      let metaDescription = document.querySelector('meta[name="description"]');
      if (!metaDescription) {
        metaDescription = document.createElement("meta");
        metaDescription.setAttribute("name", "description");
        document.head.appendChild(metaDescription);
      }
      metaDescription.setAttribute("content", description);
    }
  }, [description]);

  useEffect(() => {
    // Set Open Graph title
    if (ogTitle || title) {
      let ogTitleElement = document.querySelector('meta[property="og:title"]');
      if (!ogTitleElement) {
        ogTitleElement = document.createElement("meta");
        ogTitleElement.setAttribute("property", "og:title");
        document.head.appendChild(ogTitleElement);
      }
      ogTitleElement.setAttribute("content", ogTitle || title);
    }
  }, [ogTitle, title]);

  useEffect(() => {
    // Set Open Graph description
    if (ogDescription || description) {
      let ogDescElement = document.querySelector(
        'meta[property="og:description"]',
      );
      if (!ogDescElement) {
        ogDescElement = document.createElement("meta");
        ogDescElement.setAttribute("property", "og:description");
        document.head.appendChild(ogDescElement);
      }
      ogDescElement.setAttribute("content", ogDescription || description);
    }
  }, [ogDescription, description]);

  useEffect(() => {
    // Set canonical URL
    if (canonicalUrl) {
      let canonical = document.querySelector('link[rel="canonical"]');
      if (!canonical) {
        canonical = document.createElement("link");
        canonical.setAttribute("rel", "canonical");
        document.head.appendChild(canonical);
      }
      canonical.setAttribute("href", canonicalUrl);
    }
  }, [canonicalUrl]);
}
